<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchPolicy,
  createPolicy,
  updatePolicy,
  fetchPolicyConditions,
  fetchPolicyCondition,
  createPolicyCondition,
  updatePolicyCondition,
  deletePolicyCondition,
} from '@apis/admin/withdraw'
import { fetchCertificationTypes } from '@apis/admin/kyc'
import { fetchAssets } from '@apis/admin/asset'
import LangItem from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { LangItem },
  props: {
    policy_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      policy_conditions: [],
      certification_types: [],
      assets: [],
      condition_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([
        this.loadPolicy(),
        this.loadCertificationTypes(),
        this.loadAssets(),
      ])
        .then(() => this.loadPolicyConditions())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadPolicy() {
      const { policy_info } = await fetchPolicy(this.policy_seq)
      this.params = defaults(policy_info, {
        action_type: null,
        title: '',
        list_order: 0,
        seq: null,
        regdate: null,
      })
      this.info = policy_info
    },
    async loadPolicyConditions() {
      if (!this.info) return []

      const { data } = await fetchPolicyConditions(this.info.seq)

      this.policy_conditions = data
    },
    async loadPolicyCondition(condition_seq) {
      const { data } = await fetchPolicyCondition(this.info.seq, condition_seq)

      this.condition_params = data
    },
    async loadCertificationTypes() {
      const { data } = await fetchCertificationTypes()

      this.certification_types = data
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updatePolicy : createPolicy
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('withdraw_policy_info.policy_modified')
        : this.$t('withdraw_policy_info.policy_registered')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddCondition() {
      this.policy_conditions.push({
        list_order: 0,
        policy_seq: this.info.seq,
        condition_type: undefined,
        certification_type_seq: undefined,
        asset_seq: undefined,
      })
    },
    async handleDeleteCondition(idx) {
      this.loading = true
      try {
        if (this.policy_conditions[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deletePolicyCondition(
            this.info.seq,
            this.policy_conditions[idx].seq
          )
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.policy_conditions.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitCondition(idx) {
      this.loading = true
      const params = this.policy_conditions[idx]
      ;(params.seq ? updatePolicyCondition : createPolicyCondition)(params)
        .then(({ seq }) => fetchPolicyCondition(this.info.seq, seq))
        .then(({ condition_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.policy_conditions, idx, { ...condition_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('withdraw_policy_info.title') }}</div></th
              >
              <td colspan="3"
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{
                  $t('withdraw_policy_info.type_of_execution')
                }}</div></th
              >
              <td>
                <select v-model="params.action_type">
                  <option :value="null">{{
                    $t('withdraw_policy_info.choice')
                  }}</option>
                  <option
                    v-for="val in constants.withdraw.policy.action_type"
                    :key="val[0]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </td>
              <th scope="row"
                ><div>{{
                  $t('withdraw_policy_info.inspection_priority')
                }}</div></th
              >
              <td
                ><input
                  v-model="params.list_order"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              /></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('withdraw_policy_info.condition')"
      name="CONDITION"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">{{ $t('withdraw_policy_info.sequence') }}</th>
            <th scope="col">{{ $t('withdraw_policy_info.must') }}</th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddCondition"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(condition, idx) in policy_conditions"
            :key="condition.seq ? `${condition.seq}` : `create-${idx}`"
          >
            <td :class="[$style.tCenter, $style.nowrap]">
              <input
                v-model="condition.list_order"
                type="text"
                :class="[$style.inputTypeText, $style.w40, $style.tRight]"
              />
            </td>
            <td :class="$style.wide">
              <select
                v-if="
                  condition.condition_type ===
                    'ASSET_APPROVED_DEPOSIT_CREATE_ELAPSED_TIME'
                "
                v-model="condition.asset_seq"
              >
                <option :value="undefined">{{
                  $t('admin.restriction_select')
                }}</option>
                <option
                  v-for="{ seq, title } in assets"
                  :key="seq"
                  :value="seq"
                >
                  {{ title }}
                </option>
              </select>
              <select
                v-model="condition.condition_type"
                :disabled="condition.seq"
              >
                <option :value="undefined">{{
                  $t('admin.restriction_select')
                }}</option>
                <option
                  v-for="[type, type_title] in constants.withdraw
                    .policy_condition.condition_type"
                  :key="type"
                  :value="type"
                >
                  {{ type_title }}
                </option>
              </select>
              <template v-if="condition.condition_type === 'SECURITY_LEVEL'">
                이
                <input
                  v-model="condition.security_level"
                  type="text"
                  :class="[$style.inputTypeText, $style.w40]"
                />
              </template>
              <template
                v-else-if="condition.condition_type === 'REQUEST_AMOUNT'"
              >
                이
                <input
                  v-model="condition.request_amount"
                  type="text"
                  :class="[$style.inputTypeText, $style.w200, $style.tRight]"
                />
              </template>
              <template v-else-if="condition.condition_type === 'MEMBER'">
                고유 번호가
                <input
                  v-model="condition.member_seq"
                  type="text"
                  :class="[$style.inputTypeText, $style.w60]"
                />
              </template>
              <template v-else-if="condition.condition_type === 'TIME'">
                가 GMT
                <input
                  v-model="condition.time"
                  placeholder="00:00"
                  type="text"
                  :class="[$style.inputTypeText, $style.w60]"
                />
              </template>
              <template v-else-if="condition.condition_type === 'ASSET'">
                이
                <select v-model="condition.asset_seq">
                  <option :value="undefined">{{
                    $t('admin.restriction_select')
                  }}</option>
                  <option
                    v-for="{ seq, title } in assets"
                    :key="seq"
                    :value="seq"
                  >
                    {{ title }}
                  </option>
                </select>
              </template>
              <template
                v-else-if="
                  condition.condition_type ===
                    'ASSET_APPROVED_DEPOSIT_CREATE_ELAPSED_TIME'
                "
              >
                이
                <input
                  v-model="condition.elapsed_time"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
                초
              </template>
              <template
                v-if="
                  [
                    'SECURITY_LEVEL',
                    'REQUEST_AMOUNT',
                    'TIME',
                    'ASSET_APPROVED_DEPOSIT_CREATE_ELAPSED_TIME',
                  ].indexOf(condition.condition_type) !== -1
                "
              >
                &nbsp;
                <select v-model="condition.range_type">
                  <option :value="undefined">{{
                    $t('admin.restriction_select')
                  }}</option>
                  <option
                    v-for="[type, type_title] in constants.withdraw
                      .policy_condition.range_type"
                    :key="type"
                    :value="type"
                  >
                    {{ type_title }}
                  </option>
                </select>
              </template>
              이어야 함
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="condition.seq"
                href="#"
                :class="[$style.button]"
                @click="handleSubmitCondition(idx)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              <a
                v-else
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitCondition(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.red, $style.button]"
                @click="handleDeleteCondition(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!policy_conditions.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('withdraw_policy_info.msg_no_registered')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
